import { useCallback, useEffect, useMemo, useState } from 'react'
import { LogIn, LogOut, Clock, AlertCircle, CalendarCheck, Search, CheckCircle2, Timer, AlarmClock, CalendarOff, PartyPopper } from 'lucide-react'
import { apiJson, type Page } from '../api'
import { hasAnyRole } from '../auth'
import Pager from '../components/Pager'
import SortableTh from '../components/SortableTh'

interface AttendanceRecord {
  id: string | null
  username: string
  employeeId: string | null
  employeeName: string
  date: string
  checkInAt: string | null
  checkOutAt: string | null
  workedMinutes: number | null
  complete: boolean
  isWorkDay: boolean
  isHoliday: boolean
  holidayName: string | null
  lateMinutes: number | null
  earlyLeaveMinutes: number | null
  overtimeMinutes: number | null
}

interface EmployeeOption {
  id: string
  firstName: string
  lastName: string
  employeeCode: string
}

function fmtTime(iso: string | null) {
  if (!iso) return '—'
  const d = new Date(iso)
  return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
}

function fmtHours(mins: number | null) {
  if (mins === null) return '—'
  const h = Math.floor(mins / 60)
  const m = mins % 60
  return h > 0 ? `${h}h ${m}m` : `${m}m`
}

function StatusChip({ r }: { r: AttendanceRecord }) {
  if (r.complete) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700 ring-1 ring-emerald-100">
        <CheckCircle2 size={12} /> Complete
      </span>
    )
  }
  if (r.checkInAt) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-700 ring-1 ring-amber-100">
        <Timer size={12} /> Checked in
      </span>
    )
  }
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-500 ring-1 ring-slate-200">
      <Clock size={12} /> No record
    </span>
  )
}

/** Policy badges: late / early leave / overtime / holiday. */
function PolicyChips({ r }: { r: AttendanceRecord }) {
  const chips: { key: string; cls: string; icon: React.ReactNode; label: string }[] = []
  if (r.isHoliday) {
    chips.push({
      key: 'holiday',
      cls: 'bg-sky-50 text-sky-700 ring-sky-100',
      icon: <PartyPopper size={11} />,
      label: r.holidayName ?? 'Holiday',
    })
  } else if (!r.isWorkDay) {
    chips.push({
      key: 'off',
      cls: 'bg-slate-100 text-slate-500 ring-slate-200',
      icon: <CalendarOff size={11} />,
      label: 'Off day',
    })
  }
  if (r.lateMinutes !== null && r.lateMinutes > 0) {
    chips.push({
      key: 'late',
      cls: 'bg-amber-50 text-amber-700 ring-amber-100',
      icon: <AlarmClock size={11} />,
      label: `Late ${r.lateMinutes}m`,
    })
  }
  if (r.earlyLeaveMinutes !== null && r.earlyLeaveMinutes > 0) {
    chips.push({
      key: 'early',
      cls: 'bg-orange-50 text-orange-700 ring-orange-100',
      icon: <LogOut size={11} />,
      label: `Early ${r.earlyLeaveMinutes}m`,
    })
  }
  if (r.overtimeMinutes !== null && r.overtimeMinutes > 0) {
    chips.push({
      key: 'ot',
      cls: 'bg-emerald-50 text-emerald-700 ring-emerald-100',
      icon: <Timer size={11} />,
      label: `OT ${fmtHours(r.overtimeMinutes)}`,
    })
  }
  if (chips.length === 0) return <span className="text-xs text-slate-300">—</span>
  return (
    <span className="flex flex-wrap gap-1">
      {chips.map((c) => (
        <span key={c.key} className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ${c.cls}`}>
          {c.icon} {c.label}
        </span>
      ))}
    </span>
  )
}

const selectCls =
  'rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-600 outline-none transition-all focus:border-brand-400 focus:ring-4 focus:ring-brand-100'

export default function Attendance() {
  const allowTeam = hasAnyRole('admin', 'hr')
  const [tab, setTab] = useState<'mine' | 'team'>('mine')

  // today card
  const [today, setToday] = useState<AttendanceRecord | null>(null)
  const [now, setNow] = useState(new Date())
  const [acting, setActing] = useState(false)
  const [actionError, setActionError] = useState('')

  // my timesheet
  const [myPage, setMyPage] = useState<Page<AttendanceRecord> | null>(null)
  const [myPageNum, setMyPageNum] = useState(1)
  const [mySize, setMySize] = useState(10)
  const [mySort, setMySort] = useState('date')
  const [myOrder, setMyOrder] = useState<'ASC' | 'DESC'>('DESC')
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState('')

  // team view
  const [teamPage, setTeamPage] = useState<Page<AttendanceRecord> | null>(null)
  const [teamPageNum, setTeamPageNum] = useState(1)
  const [teamSize, setTeamSize] = useState(20)
  const [teamSort, setTeamSort] = useState('date')
  const [teamOrder, setTeamOrder] = useState<'ASC' | 'DESC'>('DESC')
  const [teamDate, setTeamDate] = useState('')
  const [teamQuery, setTeamQuery] = useState('')
  const [teamQ, setTeamQ] = useState('')
  const [teamEmpId, setTeamEmpId] = useState('')
  const [empOptions, setEmpOptions] = useState<EmployeeOption[]>([])
  const [teamDept, setTeamDept] = useState('')
  const [orgUnits, setOrgUnits] = useState<{ id: string; name: string }[]>([])
  const [workdaysOnly, setWorkdaysOnly] = useState(true)

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000)
    return () => clearInterval(t)
  }, [])

  useEffect(() => {
    const t = setTimeout(() => {
      setTeamQ(teamQuery.trim())
      setTeamPageNum(1)
    }, 300)
    return () => clearTimeout(t)
  }, [teamQuery])

  const fetchToday = useCallback(async () => {
    try {
      setToday(await apiJson<AttendanceRecord>('/attendance/today'))
    } catch {
      // card degrades gracefully
    }
  }, [])

  const fetchMine = useCallback(async () => {
    try {
      const params = new URLSearchParams({ page: String(myPageNum), size: String(mySize), sort: mySort, order: myOrder })
      setMyPage(await apiJson<Page<AttendanceRecord>>(`/attendance/me?${params}`))
      setLoadError('')
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Failed to load timesheet')
    } finally {
      setLoading(false)
    }
  }, [myPageNum, mySize, mySort, myOrder])

  const fetchTeam = useCallback(async () => {
    if (!allowTeam) return
    try {
      const params = new URLSearchParams({ page: String(teamPageNum), size: String(teamSize), sort: teamSort, order: teamOrder })
      if (teamDate) params.set('date', teamDate)
      if (teamQ) params.set('q', teamQ)
      if (teamEmpId) params.set('employeeId', teamEmpId)
      if (teamDept) params.set('orgUnitId', teamDept)
      if (workdaysOnly) params.set('workdaysOnly', 'true')
      setTeamPage(await apiJson<Page<AttendanceRecord>>(`/attendance?${params}`))
    } catch {
      // team tab is best-effort
    }
  }, [allowTeam, teamPageNum, teamSize, teamSort, teamOrder, teamDate, teamQ, teamEmpId, teamDept, workdaysOnly])

  // employee directory + departments for the team pickers (admin/hr only)
  useEffect(() => {
    if (!allowTeam) return
    apiJson<Page<EmployeeOption>>('/employees?size=500&sort=firstName&order=ASC')
      .then((p) => setEmpOptions(p.data))
      .catch(() => {})
    apiJson<{ id: string; name: string }[]>('/org-units')
      .then(setOrgUnits)
      .catch(() => {})
  }, [allowTeam])

  useEffect(() => {
    fetchToday()
  }, [fetchToday])
  useEffect(() => {
    fetchMine()
  }, [fetchMine])
  useEffect(() => {
    fetchTeam()
  }, [fetchTeam])

  const act = async (kind: 'check-in' | 'check-out') => {
    setActing(true)
    setActionError('')
    try {
      await apiJson(`/attendance/${kind}`, { method: 'POST' })
      await Promise.all([fetchToday(), fetchMine(), fetchTeam()])
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Action failed')
    } finally {
      setActing(false)
    }
  }

  const myRows = useMemo(() => myPage?.data ?? [], [myPage])
  const teamRows = useMemo(() => teamPage?.data ?? [], [teamPage])

  const handleSort = (
    field: string,
    sort: string,
    order: 'ASC' | 'DESC',
    setSort: (v: string) => void,
    setOrder: (v: 'ASC' | 'DESC') => void,
    setPageNum: (v: number) => void,
  ) => {
    if (sort === field) {
      setOrder(order === 'ASC' ? 'DESC' : 'ASC')
    } else {
      setSort(field)
      setOrder(field === 'date' ? 'DESC' : 'ASC')
    }
    setPageNum(1)
  }

  const elapsed = useMemo(() => {
    if (!today?.checkInAt || today.checkOutAt) return null
    return Math.max(0, Math.floor((now.getTime() - new Date(today.checkInAt).getTime()) / 60000))
  }, [today, now])

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-200 border-t-brand-600" />
      </div>
    )
  }

  return (
    <div className="space-y-5">
      {/* Today card */}
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="flex flex-col items-center gap-4 bg-gradient-to-br from-brand-600 via-brand-500 to-brand-700 px-6 py-8 text-white sm:flex-row sm:justify-between">
          <div className="text-center sm:text-left">
            <p className="text-xs font-semibold uppercase tracking-widest text-white/70">
              {now.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
            </p>
            <p className="mt-1 text-4xl font-extrabold tabular-nums tracking-tight">
              {now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
            </p>
            <p className="mt-1 text-sm text-white/80">
              {today?.complete
                ? `Done for today — worked ${fmtHours(today.workedMinutes)}`
                : today?.checkInAt
                  ? `Checked in at ${fmtTime(today.checkInAt)} · ${fmtHours(elapsed)} elapsed`
                  : 'Not checked in yet'}
            </p>
            {today?.isHoliday && (
              <p className="mt-1.5 inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 text-xs font-semibold ring-1 ring-white/30">
                <PartyPopper size={12} /> Public holiday — {today.holidayName}
              </p>
            )}
            {(today?.lateMinutes ?? 0) > 0 && (
              <p className="mt-1.5 ml-2 inline-flex items-center gap-1.5 rounded-full bg-amber-400/20 px-3 py-1 text-xs font-semibold ring-1 ring-amber-200/40">
                <AlarmClock size={12} /> Late by {today!.lateMinutes}m
              </p>
            )}
          </div>
          <div>
            {!today?.checkInAt ? (
              <button
                onClick={() => act('check-in')}
                disabled={acting}
                className="flex items-center gap-2 rounded-xl bg-white px-6 py-3 text-sm font-bold text-brand-700 shadow-lg transition-all hover:shadow-xl hover:brightness-105 disabled:opacity-60"
              >
                <LogIn size={17} />
                {acting ? 'Checking in…' : 'Check In'}
              </button>
            ) : !today.checkOutAt ? (
              <button
                onClick={() => act('check-out')}
                disabled={acting}
                className="flex items-center gap-2 rounded-xl bg-white px-6 py-3 text-sm font-bold text-brand-700 shadow-lg transition-all hover:shadow-xl hover:brightness-105 disabled:opacity-60"
              >
                <LogOut size={17} />
                {acting ? 'Checking out…' : 'Check Out'}
              </button>
            ) : (
              <div className="flex items-center gap-2 rounded-xl bg-white/15 px-5 py-3 text-sm font-semibold ring-1 ring-white/30">
                <CheckCircle2 size={17} />
                {fmtTime(today.checkInAt)} → {fmtTime(today.checkOutAt)}
              </div>
            )}
          </div>
        </div>
      </div>

      {(actionError || loadError) && (
        <div className="flex items-center gap-2.5 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
          <AlertCircle size={16} className="shrink-0" />
          {actionError || loadError}
        </div>
      )}

      {/* Tabs */}
      {allowTeam && (
        <div className="flex gap-1 rounded-xl bg-slate-100 p-1 sm:w-fit">
          <button
            onClick={() => setTab('mine')}
            className={`rounded-lg px-4 py-2 text-xs font-semibold transition-all ${tab === 'mine' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
          >
            My timesheet
          </button>
          <button
            onClick={() => setTab('team')}
            className={`rounded-lg px-4 py-2 text-xs font-semibold transition-all ${tab === 'team' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
          >
            Team
          </button>
        </div>
      )}

      {/* My timesheet */}
      {tab === 'mine' && (
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px]">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50/60">
                  <SortableTh label="Date" field="date" sort={mySort} order={myOrder} onSort={(f) => handleSort(f, mySort, myOrder, setMySort, setMyOrder, setMyPageNum)} />
                  <SortableTh label="Check in" field="checkInAt" sort={mySort} order={myOrder} onSort={(f) => handleSort(f, mySort, myOrder, setMySort, setMyOrder, setMyPageNum)} />
                  <SortableTh label="Check out" field="checkOutAt" sort={mySort} order={myOrder} onSort={(f) => handleSort(f, mySort, myOrder, setMySort, setMyOrder, setMyPageNum)} />
                  <th className="px-5 py-3.5 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-400">Worked</th>
                  <th className="px-5 py-3.5 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-400">Status</th>
                  <th className="px-5 py-3.5 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-400">Flags</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {myRows.map((r) => (
                  <tr key={r.id ?? r.date} className="transition-colors hover:bg-slate-50/70">
                    <td className="px-5 py-3.5 text-sm font-semibold">{r.date}</td>
                    <td className="px-5 py-3.5 text-sm text-slate-500">{fmtTime(r.checkInAt)}</td>
                    <td className="px-5 py-3.5 text-sm text-slate-500">{fmtTime(r.checkOutAt)}</td>
                    <td className="px-5 py-3.5 text-sm font-semibold">{fmtHours(r.workedMinutes)}</td>
                    <td className="px-5 py-3.5"><StatusChip r={r} /></td>
                    <td className="px-5 py-3.5"><PolicyChips r={r} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {myRows.length === 0 && (
            <div className="flex flex-col items-center py-14 text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-100 text-slate-400">
                <CalendarCheck size={22} />
              </div>
              <p className="mt-3 text-sm font-semibold">No attendance records yet</p>
              <p className="mt-1 text-xs text-slate-400">Check in above to start your timesheet.</p>
            </div>
          )}
          <Pager
            page={myPageNum}
            totalPages={myPage?.totalPages ?? 1}
            total={myPage?.total ?? 0}
            size={mySize}
            onPage={setMyPageNum}
            onSize={(s) => { setMySize(s); setMyPageNum(1) }}
          />
        </div>
      )}

      {/* Team view */}
      {tab === 'team' && allowTeam && (
        <div className="space-y-4">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <div className="relative">
              <Search size={15} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={teamQuery}
                onChange={(e) => setTeamQuery(e.target.value)}
                placeholder="Search name or username…"
                className="w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-10 pr-4 text-sm outline-none transition-all placeholder:text-slate-400 focus:border-brand-400 focus:ring-4 focus:ring-brand-100 sm:w-64"
              />
            </div>
            <select
              value={teamEmpId}
              onChange={(e) => { setTeamEmpId(e.target.value); setTeamPageNum(1) }}
              className={selectCls}
            >
              <option value="">All employees</option>
              {empOptions.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.firstName} {e.lastName} ({e.employeeCode})
                </option>
              ))}
            </select>
            <select
              value={teamDept}
              onChange={(e) => { setTeamDept(e.target.value); setTeamPageNum(1) }}
              className={selectCls}
            >
              <option value="">All departments</option>
              {orgUnits.map((o) => (
                <option key={o.id} value={o.id}>{o.name}</option>
              ))}
            </select>
            <input
              type="date"
              value={teamDate}
              onChange={(e) => { setTeamDate(e.target.value); setTeamPageNum(1) }}
              className={selectCls}
            />
            <button
              onClick={() => { setWorkdaysOnly(!workdaysOnly); setTeamPageNum(1) }}
              className={`rounded-xl px-3 py-2.5 text-xs font-semibold ring-1 transition-all ${
                workdaysOnly
                  ? 'bg-brand-50 text-brand-700 ring-brand-200'
                  : 'bg-white text-slate-500 ring-slate-200 hover:bg-slate-50'
              }`}
              title="Hide non-working days per the company work week"
            >
              Work days only
            </button>
            {(teamDate || teamEmpId || teamDept) && (
              <button
                onClick={() => { setTeamDate(''); setTeamEmpId(''); setTeamDept(''); setTeamPageNum(1) }}
                className="text-xs font-semibold text-brand-600 hover:text-brand-700"
              >
                Clear filters
              </button>
            )}
          </div>

          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[760px]">
                <thead>
                  <tr className="border-b border-slate-100 bg-slate-50/60">
                    <SortableTh label="Employee" field="employeeName" sort={teamSort} order={teamOrder} onSort={(f) => handleSort(f, teamSort, teamOrder, setTeamSort, setTeamOrder, setTeamPageNum)} />
                    <SortableTh label="Date" field="date" sort={teamSort} order={teamOrder} onSort={(f) => handleSort(f, teamSort, teamOrder, setTeamSort, setTeamOrder, setTeamPageNum)} />
                    <th className="px-5 py-3.5 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-400">Check in</th>
                    <th className="px-5 py-3.5 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-400">Check out</th>
                    <th className="px-5 py-3.5 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-400">Worked</th>
                    <th className="px-5 py-3.5 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-400">Status</th>
                    <th className="px-5 py-3.5 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-400">Flags</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {teamRows.map((r) => (
                    <tr key={r.id ?? `${r.username}-${r.date}`} className="transition-colors hover:bg-slate-50/70">
                      <td className="px-5 py-3.5">
                        <p className="text-sm font-semibold">{r.employeeName || r.username}</p>
                        <p className="text-xs text-slate-400">{r.username}</p>
                      </td>
                      <td className="px-5 py-3.5 text-sm font-semibold">{r.date}</td>
                      <td className="px-5 py-3.5 text-sm text-slate-500">{fmtTime(r.checkInAt)}</td>
                      <td className="px-5 py-3.5 text-sm text-slate-500">{fmtTime(r.checkOutAt)}</td>
                      <td className="px-5 py-3.5 text-sm font-semibold">{fmtHours(r.workedMinutes)}</td>
                      <td className="px-5 py-3.5"><StatusChip r={r} /></td>
                      <td className="px-5 py-3.5"><PolicyChips r={r} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {teamRows.length === 0 && (
              <div className="flex flex-col items-center py-14 text-center">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-100 text-slate-400">
                  <CalendarCheck size={22} />
                </div>
                <p className="mt-3 text-sm font-semibold">No team records</p>
                <p className="mt-1 text-xs text-slate-400">Attendance records will appear here as people check in.</p>
              </div>
            )}
            <Pager
              page={teamPageNum}
              totalPages={teamPage?.totalPages ?? 1}
              total={teamPage?.total ?? 0}
              size={teamSize}
              onPage={setTeamPageNum}
              onSize={(s) => { setTeamSize(s); setTeamPageNum(1) }}
            />
          </div>
        </div>
      )}
    </div>
  )
}
